import { notifications } from '@mantine/notifications'
import axios, { AxiosInstance } from 'axios'
import { BACKEND_TRANSLATION_KEYS } from 'common/utils'
import { t } from 'i18next'
import { auth } from 'services'

// Key for storing 401 error info in sessionStorage
const UNAUTHORIZED_ERROR_KEY = 'unauthorized_error'

const LOGOUT_URL = 'logout'

const instance: AxiosInstance = axios.create({
  baseURL: window.BASE_API_URL || import.meta.env.VITE_BASE_API_URL || '',
  timeout: 360000,
})

instance.interceptors.request.use(
  (config) => {
    if (!config.headers.Authorization) {
      const token = localStorage.getItem('access_token')
      config.headers.Authorization = `Bearer ${token}`
    }

    config.withCredentials = true

    return config
  },
  () => {},
)

instance.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    if (error?.response) {
      // Special handling for 401 Unauthorized
      if (error.response.status === 401) {
        // Store error info in sessionStorage for display after redirect
        const errorInfo = {
          message: error.response?.data?.title || 'Unauthorized access',
          timestamp: Date.now(),
        }
        sessionStorage.setItem(UNAUTHORIZED_ERROR_KEY, JSON.stringify(errorInfo))

        // Clear all authentication data using existing auth service
        auth.handleAutomaticLogout()

        // Immediate redirect using replace (more aggressive than href)
        window.location.replace('/')

        // Return a never-resolving promise to prevent further processing
        return new Promise(() => {})
      }

      if ([400, 402, 403, 404].includes(error.response.status)) {
        const hasTranslationKeys = BACKEND_TRANSLATION_KEYS.includes(
          error.response?.data?.details,
        )

        if (!error.config.url?.includes(LOGOUT_URL)) {
          notifications.show({
            color: 'red',
            message: hasTranslationKeys
              ? t(`backend.${error.response?.data?.details}`)
              : error.response?.data?.details,
          })
        }

        return Promise.reject(error.response)
      }

      if (error.response.status === 500) {
        if (!error.config.url?.includes(LOGOUT_URL)) {
          notifications.show({
            color: 'red',
            message: error.response?.data?.title,
          })
        }

        return Promise.reject(error.response)
      }
    }
  },
)

export default instance

// Export the key for potential use in other parts of the app
export { UNAUTHORIZED_ERROR_KEY }
